const record = (value) => value && typeof value === "object" && !Array.isArray(value) ? value : {};
export const positive = (value, integer = false) => typeof value !== "boolean" && value !== ""
  && Number.isFinite(Number(value)) && Number(value) > 0 && (!integer || Number.isInteger(Number(value)));

// File totals are not divided among trackers or connectors.
export function ondConnectionState(chars = {}) {
  const model = chars.ondModel || {};
  const facts = chars.ondConnectionFromFile || { n_mppt: model.n_mppt ?? chars["Num MPPTs"],
    n_inputs: model.n_inputs, operating_a: chars.Idcmax ?? model.idc_max_a };
  const supplied = record(chars.ondConnection);
  const count = positive(facts.n_mppt, true) ? Number(facts.n_mppt) : positive(supplied.n_mppt, true) ? Number(supplied.n_mppt) : 0;
  const terminals = positive(facts.n_inputs, true) ? Number(facts.n_inputs) : positive(supplied.n_inputs, true) ? Number(supplied.n_inputs) : 0;
  const mode = ["direct", "combiner"].includes(supplied.mode) ? supplied.mode : "unknown";
  const known = { n_mppt: positive(facts.n_mppt, true), n_inputs: positive(facts.n_inputs, true), mppts: [], inputs: [] };
  let needsSource = !known.n_mppt || !known.n_inputs;
  const groups = Array.from({ length: Math.min(64, count) }, (_, index) => {
    const row = record(supplied.mppts?.[index]);
    const mask = { operating_a: count === 1 && positive(facts.operating_a), short_circuit_a: count === 1 && positive(facts.short_circuit_a) };
    known.mppts.push(mask);
    needsSource ||= !mask.operating_a || !mask.short_circuit_a;
    return { operating_a: mask.operating_a ? facts.operating_a : row.operating_a ?? "",
      short_circuit_a: mask.short_circuit_a ? facts.short_circuit_a : row.short_circuit_a ?? "" };
  });
  const inputs = Array.from({ length: Math.min(256, terminals) }, (_, index) => {
    const row = record(supplied.inputs?.[index]);
    const unique = mode === "direct" && count === 1 && terminals === 1;
    const mask = { mppt: count === 1, operating_a: unique && positive(facts.operating_a), short_circuit_a: unique && positive(facts.short_circuit_a) };
    known.inputs.push(mask);
    needsSource ||= !mask.mppt || !mask.operating_a || !mask.short_circuit_a || mode === "combiner";
    return { ...row, mppt: mask.mppt ? 1 : row.mppt ?? "",
      operating_a: mask.operating_a ? facts.operating_a : row.operating_a ?? "",
      short_circuit_a: mask.short_circuit_a ? facts.short_circuit_a : row.short_circuit_a ?? "",
      max_strings: row.max_strings ?? "", voltage_v: row.voltage_v ?? "" };
  });
  const totalOperating = positive(facts.operating_a) ? Number(facts.operating_a)
    : count === 1 ? groups[0]?.operating_a : supplied.total_operating_a;
  const definition = { ...supplied, mode, n_mppt: count, n_inputs: terminals, mppts: groups, inputs,
    total_operating_a: totalOperating ?? "",
    source: needsSource ? supplied.source || "" : `OND: ${chars.ondSource?.filename || "imported file"}` };
  return { definition, known, needsSource, facts };
}

export function ondConnectionComplete(chars) {
  const { definition: d } = ondConnectionState(chars);
  return Boolean(["direct", "combiner"].includes(d.mode) && typeof d.source === "string" && d.source.trim()
    && d.n_mppt > 0 && d.n_mppt <= 64 && d.n_inputs > 0 && d.n_inputs <= 256
    && d.mppts.every((row) => positive(row.operating_a) && positive(row.short_circuit_a)
      && Number(row.short_circuit_a) >= Number(row.operating_a) && Number(row.operating_a) <= Number(d.total_operating_a))
    && d.inputs.every((row) => positive(row.mppt, true) && Number(row.mppt) <= d.n_mppt
      && positive(row.operating_a) && positive(row.short_circuit_a) && Number(row.short_circuit_a) >= Number(row.operating_a)
      && (d.mode === "direct" || (positive(row.max_strings, true) && positive(row.voltage_v))))
    && new Set(d.inputs.map((row) => Number(row.mppt))).size === d.n_mppt);
}

// Keep inherited file values out of supplements, particularly when switching
// from a direct input to an external combiner with its own current ratings.
export function connectionSupplement(state, patch) {
  const next = { ...state.definition, ...patch };
  const modeChanged = patch.mode && patch.mode !== state.definition.mode;
  const clean = (row, mask) => Object.fromEntries(Object.entries(row).filter(([key]) => !mask?.[key]));
  next.mppts = next.mppts.map((row, i) => clean(row, state.known.mppts[i]));
  next.inputs = modeChanged ? [] : next.inputs.map((row, i) => clean(row, state.known.inputs[i]));
  if (state.known.n_mppt) delete next.n_mppt;
  if (state.known.n_inputs) delete next.n_inputs;
  if (!state.needsSource) delete next.source;
  if (positive(state.facts.operating_a) || state.definition.n_mppt === 1) delete next.total_operating_a;
  return next;
}
